import { forwardRef, useEffect, useState, type HTMLAttributes, type ImgHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./lib/cn.js";

const avatarVariants = cva(
 [
 "relative inline-flex items-center justify-center overflow-hidden",
 "bg-[var(--pp-border)] text-[var(--pp-text)] select-none",
 ],
 {
 variants: {
 size: {
 sm: "h-7 w-7 text-[var(--pp-font-sm)]",
 md: "h-10 w-10 text-[var(--pp-font-md)]",
 lg: "h-14 w-14 text-[var(--pp-font-md)]",
 },
 shape: {
 circle: "rounded-full",
 square: "",
 },
 },
 defaultVariants: {
 size: "md",
 shape: "circle",
 },
 },
);

export interface AvatarProps
 extends HTMLAttributes<HTMLSpanElement>,
 VariantProps<typeof avatarVariants> {
 size?: "sm" | "md" | "lg";
 shape?: "circle" | "square";
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
 ({ className, size, shape, ...props }, ref) => (
 <span
 ref={ref}
 role="img"
 className={cn(avatarVariants({ size, shape }), className)}
 {...props}
 />
 ),
);
Avatar.displayName = "Avatar";

export interface AvatarImageProps extends ImgHTMLAttributes<HTMLImageElement> {
 src: string;
 alt: string;
}

export const AvatarImage = forwardRef<HTMLImageElement, AvatarImageProps>(
 ({ className, src, alt, onError, ...props }, ref) => {
 const [failed, setFailed] = useState(false);
 useEffect(() => {
 setFailed(false);
 }, [src]);
 if (failed) return null;
 return (
 <img
 ref={ref}
 src={src}
 alt={alt}
 className={cn("h-full w-full object-cover", className)}
 onError={(e) => {
 setFailed(true);
 onError?.(e);
 }}
 {...props}
 />
 );
 },
);
AvatarImage.displayName = "AvatarImage";

export interface AvatarFallbackProps extends HTMLAttributes<HTMLSpanElement> {
 /** Two-character monogram. Rendered inside the avatar. */
 initials: string;
}

export const AvatarFallback = forwardRef<HTMLSpanElement, AvatarFallbackProps>(
 ({ className, initials, ...props }, ref) => (
 <span
 ref={ref}
 aria-hidden
 className={cn(
 "absolute inset-0 flex items-center justify-center font-medium tracking-tight",
 className,
 )}
 {...props}
 >
 {initials.slice(0, 2).toUpperCase()}
 </span>
 ),
);
AvatarFallback.displayName = "AvatarFallback";

export { avatarVariants };
